/**
 * Hook: useDocumentTypes
 * CRUD de tipos de documento via RPCs server-side
 *
 * @version 2.0.0
 * RELEASE: 2026-05-10 — Migração para RPCs (get_document_types_list, create_document_type,
 *   update_document_type, delete_document_type). Sem acesso direto à tabela.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { showToast } from '@/lib/toast';

const logger = createLogger('DocumentTypes');

/** Tipo de documento */
export interface DocumentType {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  category: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/** Input para criar/editar tipo de documento */
export interface DocumentTypeInput {
  name: string;
  description?: string;
  category?: string;
  is_active?: boolean;
  sort_order?: number;
}

/**
 * Hook para CRUD de tipos de documento
 */
export function useDocumentTypes() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // Listagem via RPC
  const { data: documentTypes = [], isLoading } = useQuery({
    queryKey: ['document-types', tenantId],
    queryFn: async (): Promise<DocumentType[]> => {
      if (!tenantId) return [];

      const { data, error } = await supabase.rpc('get_document_types_list', {
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao buscar tipos de documento:', error.message);
        throw error;
      }

      return (data || []) as DocumentType[];
    },
    enabled: !!tenantId,
  });

  // Criação via RPC
  const createDocumentType = useMutation({
    mutationFn: async (input: DocumentTypeInput) => {
      if (!tenantId) throw new Error('Tenant não identificado');

      const payload = {
        name: input.name,
        description: input.description ?? null,
        category: input.category ?? 'geral',
        is_active: input.is_active ?? true,
        sort_order: input.sort_order ?? 0,
      };

      const { data, error } = await supabase.rpc('create_document_type', {
        p_payload: payload as any,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao criar tipo de documento:', error.message);
        throw error;
      }

      return { id: data as string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-types', tenantId] });
      showToast.success('Tipo de documento criado com sucesso');
    },
    onError: (error: Error) => {
      showToast.error('Erro ao criar tipo de documento', error.message);
    },
  });

  // Atualização via RPC
  const updateDocumentType = useMutation({
    mutationFn: async (input: DocumentTypeInput & { id: string }) => {
      if (!tenantId) throw new Error('Tenant não identificado');

      const payload: Record<string, unknown> = {
        name: input.name,
        description: input.description ?? null,
        category: input.category ?? 'geral',
        is_active: input.is_active,
        sort_order: input.sort_order,
      };

      const { error } = await supabase.rpc('update_document_type', {
        p_id: input.id,
        p_payload: payload as any,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao atualizar tipo de documento:', error.message);
        throw error;
      }

      return { id: input.id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-types', tenantId] });
      showToast.success('Tipo de documento atualizado');
    },
    onError: (error: Error) => {
      showToast.error('Erro ao atualizar', error.message);
    },
  });

  // Exclusão via RPC
  const deleteDocumentType = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error('Tenant não identificado');

      const { error } = await supabase.rpc('delete_document_type', {
        p_id: id,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao excluir tipo de documento:', error.message);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-types', tenantId] });
      showToast.success('Tipo de documento excluído');
    },
    onError: (error: Error) => {
      showToast.error('Erro ao excluir', error.message);
    },
  });

  return {
    documentTypes,
    isLoading,
    createDocumentType,
    updateDocumentType,
    deleteDocumentType,
  };
}
